import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, FileDown, Factory, Tag, FileText, FileSignature, Trash2, Ban, RefreshCw } from "lucide-react";
import api, { formatApiError, downloadFromApi, extractErrorMessage } from "../lib/api";
import PageHeader from "../components/PageHeader";
import RecordProductionModal from "../components/RecordProductionModal";
import LoadTagsModal from "../components/LoadTagsModal";
import PackingListsModal from "../components/PackingListsModal";
import FreightDataModal from "../components/FreightDataModal";
import AdminDeleteModal from "../components/AdminDeleteModal";
import Modal from "../components/Modal";
import SyncNowModal from "../components/SyncNowModal";
import { useAuth } from "../contexts/AuthContext";
import { fmtDate, fmtMoney } from "../lib/format";
import { toast } from "sonner";

function statusBadge(status, eta) {
    if (status === "completed")
        return <span className="odr-badge odr-badge-completed">Completed</span>;
    if (status === "awaiting_shipment")
        return <span className="odr-badge odr-badge-awaiting">Awaiting Shipment</span>;
    // open — color by ETA
    try {
        const e = new Date(eta).getTime();
        const now = Date.now();
        const days = (e - now) / 86400000;
        if (days < 0) return <span className="odr-badge odr-badge-past">Open · Past ETA</span>;
        if (days < 3) return <span className="odr-badge odr-badge-near">Open · Near ETA</span>;
    } catch (err) {
        console.error("statusBadge: failed to parse ETA", eta, err);
    }
    return <span className="odr-badge odr-badge-open">Open</span>;
}

async function downloadLatestOC(orderId, setError) {
    try {
        const { data } = await api.get("/documents", {
            params: { order_id: orderId, type: "order_confirmation" },
        });
        if (!data.length) {
            setError("No Order Confirmation PDF found for this order.");
            return;
        }
        const doc = data[0]; // most recent (sorted desc)
        await downloadFromApi(`/documents/${doc.id}/download`, doc.filename);
    } catch (err) {
        setError(formatApiError(err?.response?.data?.detail) || err.message);
    }
}

function ActionButton({ label, icon: Icon, onClick, disabled, title, testId }) {
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onClick?.();
            }}
            disabled={disabled}
            title={title}
            className={`inline-flex items-center justify-center gap-1 px-2 py-1 rounded-sm border text-[11px] font-semibold tracking-wider
                ${
                    disabled
                        ? "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"
                        : "border-gray-300 text-gray-800 hover:bg-gray-100"
                }`}
            data-testid={testId}
        >
            <Icon className="w-3 h-3" /> {label}
        </button>
    );
}

export default function Orders() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [syncOpen, setSyncOpen] = useState(false);
    const [orders, setOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [rpOrder, setRpOrder] = useState(null); // order to open RP modal for
    const [ltOrder, setLtOrder] = useState(null); // order to open LT modal for
    const [plOrder, setPlOrder] = useState(null); // order to open PL modal for
    const [freightCtx, setFreightCtx] = useState(null); // {order, rp} for LTL freight modal
    const [deletingOrder, setDeletingOrder] = useState(null);
    const [cancellingOrder, setCancellingOrder] = useState(null);

    const [search, setSearch] = useState("");
    const [customerFilter, setCustomerFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [productionFilter, setProductionFilter] = useState("");
    const [includeVoided, setIncludeVoided] = useState(false);

    useEffect(() => {
        api.get("/customers", { params: { active: true } }).then((r) => setCustomers(r.data));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search.trim()) params.q = search.trim();
            if (customerFilter) params.customer_id = customerFilter;
            if (statusFilter) params.status = statusFilter;
            if (productionFilter) params.production_type = productionFilter;
            if (includeVoided) params.include_voided = true;
            const { data } = await api.get("/orders", { params });
            setOrders(data);
            setError("");
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, [search, customerFilter, statusFilter, productionFilter, includeVoided]);

    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [load]);

    return (
        <div data-testid="orders-page">
            <PageHeader
                title="Orders"
                subtitle="Active operational orders — completed orders live in Reports"
                actions={
                    <div className="flex items-center gap-2">
                        {isAdmin && (
                            <button
                                onClick={() => setSyncOpen(true)}
                                className="odr-btn inline-flex items-center gap-2 rounded-sm border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 text-sm font-semibold px-4 py-2"
                                data-testid="orders-sync-now-button"
                                title="Import purchase orders from OneDrive (admin only)"
                            >
                                <RefreshCw className="w-4 h-4" /> Sync Now
                            </button>
                        )}
                        <Link
                            to="/orders/new"
                            className="odr-btn inline-flex items-center gap-2 rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4"
                            data-testid="orders-add-button"
                        >
                            <Plus className="w-4 h-4" /> New Order
                        </Link>
                    </div>
                }
            />

            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap gap-3 items-end">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search PO or order #…"
                        className="odr-input w-full rounded-sm border border-gray-300 pl-9 pr-3 text-sm"
                        data-testid="orders-search"
                    />
                </div>
                <select
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value)}
                    className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white min-w-[180px]"
                    data-testid="orders-customer-filter"
                >
                    <option value="">All customers</option>
                    {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white"
                    data-testid="orders-status-filter"
                >
                    <option value="">Any status</option>
                    <option value="open">Open</option>
                    <option value="awaiting_shipment">Awaiting Shipment</option>
                    <option value="completed">Completed</option>
                </select>
                <select
                    value={productionFilter}
                    onChange={(e) => setProductionFilter(e.target.value)}
                    className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white"
                    data-testid="orders-production-filter"
                >
                    <option value="">Any production</option>
                    <option value="in_house">In-House</option>
                    <option value="farm_out">Farm-Out</option>
                </select>
                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={includeVoided}
                        onChange={(e) => setIncludeVoided(e.target.checked)}
                        data-testid="orders-include-voided"
                    />
                    Show voided
                </label>
            </div>

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="odr-table" data-testid="orders-table">
                    <thead>
                        <tr>
                            <th>Order #</th>
                            <th>Customer</th>
                            <th>Customer PO</th>
                            <th>ETA</th>
                            <th>Status</th>
                            <th className="text-right">Ordered</th>
                            <th className="text-right">Produced</th>
                            <th className="text-right">Remaining</th>
                            <th className="text-right">Value</th>
                            <th>Blind</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={11} className="text-center text-gray-500">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && orders.length === 0 && (
                            <tr>
                                <td colSpan={11} className="text-center text-gray-500">
                                    No orders found.
                                </td>
                            </tr>
                        )}
                        {orders.map((o) => {
                            const ordered = o.totals?.qty_ordered || 0;
                            const produced = o.production?.qty_produced || 0;
                            const remaining = Math.max(ordered - produced, 0);
                            const rpDisabled = o.status === "completed";
                            return (
                                <tr
                                    key={o.id}
                                    onClick={() => navigate(`/orders/${o.id}`)}
                                    className={`cursor-pointer ${o.voided ? "opacity-40 line-through" : ""}`}
                                    data-testid={`order-row-${o.internal_number}`}
                                >
                                    <td className="odr-mono text-xs font-semibold">
                                        {o.internal_number}
                                        {o.voided && (
                                            <span
                                                className="ml-2 odr-badge odr-badge-past"
                                                title={`Voided by ${o.voided_by}: ${o.void_reason}`}
                                            >
                                                VOIDED
                                            </span>
                                        )}
                                    </td>
                                    <td>{o.customer_name_snapshot}</td>
                                    <td className="odr-mono text-xs">{o.customer_po}</td>
                                    <td className="text-xs">{fmtDate(o.eta)}</td>
                                    <td>{statusBadge(o.status, o.eta)}</td>
                                    <td className="text-right odr-mono">
                                        {Number(ordered).toLocaleString()}
                                    </td>
                                    <td className="text-right odr-mono">
                                        {Number(produced).toLocaleString()}
                                    </td>
                                    <td className="text-right odr-mono">
                                        {remaining.toLocaleString()}
                                    </td>
                                    <td className="text-right odr-mono">
                                        {fmtMoney(o.totals?.value)}
                                    </td>
                                    <td>
                                        {o.blind_shipment ? (
                                            <span className="odr-badge odr-badge-past">Yes</span>
                                        ) : (
                                            <span className="text-xs text-gray-400">—</span>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <div className="flex items-center justify-center gap-1">
                                            <ActionButton
                                                label="RP"
                                                icon={Factory}
                                                disabled={rpDisabled}
                                                onClick={() => setRpOrder(o)}
                                                title={
                                                    rpDisabled
                                                        ? "Order is completed — RP closed"
                                                        : "Record Production"
                                                }
                                                testId={`action-rp-${o.internal_number}`}
                                            />
                                            <ActionButton
                                                label="LT"
                                                icon={Tag}
                                                disabled={rpDisabled}
                                                onClick={() => setLtOrder(o)}
                                                title={
                                                    rpDisabled
                                                        ? "Order completed — Load Tags closed"
                                                        : "Generate Load Tags (Blank / RP-based / Manual)"
                                                }
                                                testId={`action-lt-${o.internal_number}`}
                                            />
                                            <ActionButton
                                                label="PL"
                                                icon={FileText}
                                                disabled={o.status === "completed"}
                                                onClick={() => setPlOrder(o)}
                                                title={
                                                    o.status === "completed"
                                                        ? "Order completed — Packing Lists closed"
                                                        : "Generate Packing List (V1 / V2 / V3 …)"
                                                }
                                                testId={`action-pl-${o.internal_number}`}
                                            />
                                            <ActionButton
                                                label="OC"
                                                icon={FileSignature}
                                                onClick={() => downloadLatestOC(o.id, setError)}
                                                title="Download latest Order Confirmation PDF"
                                                testId={`action-oc-${o.internal_number}`}
                                            />
                                            {isAdmin && (
                                                <ActionButton
                                                    label="DEL"
                                                    icon={Trash2}
                                                    onClick={() => setDeletingOrder(o)}
                                                    title="Hard-delete this order (admin)"
                                                    testId={`action-delete-${o.internal_number}`}
                                                />
                                            )}
                                            <ActionButton
                                                label="CXL"
                                                icon={Ban}
                                                disabled={
                                                    o.status === "completed"
                                                    || o.cancelled
                                                    || o.voided
                                                }
                                                onClick={() => setCancellingOrder(o)}
                                                title={
                                                    o.cancelled
                                                        ? "Order already cancelled"
                                                        : o.status === "completed"
                                                          ? "Completed orders cannot be cancelled"
                                                          : "Cancel this order (reason required, audited)"
                                                }
                                                testId={`action-cancel-${o.internal_number}`}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {rpOrder && (
                <RpLoader
                    orderId={rpOrder.id}
                    onClose={() => setRpOrder(null)}
                    onSaved={async (_createdRp, fullOrder) => {
                        const wasLtl = fullOrder?.shipping_method === "LTL";
                        setRpOrder(null);
                        await load();
                        if (wasLtl) setFreightCtx({ order: fullOrder });
                    }}
                />
            )}

            {freightCtx && (
                <FreightDataModal
                    open={!!freightCtx}
                    order={freightCtx.order}
                    onClose={() => setFreightCtx(null)}
                    onSaved={async () => {
                        await load();
                    }}
                />
            )}

            {ltOrder && (
                <LtLoader
                    orderId={ltOrder.id}
                    onClose={() => setLtOrder(null)}
                    onGenerated={async () => {
                        setLtOrder(null);
                        await load();
                    }}
                />
            )}

            {plOrder && (
                <PlLoader
                    orderId={plOrder.id}
                    onClose={() => setPlOrder(null)}
                    onGenerated={async () => {
                        setPlOrder(null);
                        await load();
                    }}
                />
            )}

            <AdminDeleteModal
                open={!!deletingOrder}
                module="orders"
                recordId={deletingOrder?.id}
                endpoint={deletingOrder ? `/orders/${deletingOrder.id}` : ""}
                label={deletingOrder ? `order ${deletingOrder.internal_number}` : ""}
                onClose={() => setDeletingOrder(null)}
                onDeleted={() => load()}
                testIdPrefix="delete-order"
            />

            <CancelOrderModal
                order={cancellingOrder}
                onClose={() => setCancellingOrder(null)}
                onCancelled={() => { setCancellingOrder(null); load(); }}
            />

            {syncOpen && (
                <SyncNowModal
                    onClose={() => setSyncOpen(false)}
                    onCompleted={() => { load(); }}
                />
            )}
        </div>
    );
}

function CancelOrderModal({ order, onClose, onCancelled }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (order) { setReason(""); setError(""); }
    }, [order]);

    if (!order) return null;
    const canFire = reason.trim().length >= 5 && !busy;

    const fire = async () => {
        setBusy(true); setError("");
        try {
            await api.post(`/orders/${order.id}/cancel`, { reason: reason.trim() });
            toast.success(`Order ${order.internal_number} cancelled`);
            onCancelled?.();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title={
                <span className="inline-flex items-center gap-2">
                    <Ban className="w-5 h-5 text-red-700" /> Cancel order
                </span>
            }
            onClose={busy ? () => {} : onClose}
            size="sm"
            testId="cancel-order-modal"
        >
            <div className="space-y-3">
                <div className="text-sm text-gray-700">
                    You are about to cancel order{" "}
                    <strong className="odr-mono">{order.internal_number}</strong>.
                    The order will remain in the system (full audit), excluded
                    from active workload views, and its global number is
                    preserved (never reused).
                </div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Reason (≥ 5 chars, recorded in audit)
                </label>
                <textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Customer requested cancellation prior to production"
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm"
                    autoFocus
                    data-testid="cancel-order-reason"
                />
                {error && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {error}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        data-testid="cancel-order-cancel-btn"
                    >
                        Keep order
                    </button>
                    <button
                        type="button"
                        onClick={fire}
                        disabled={!canFire}
                        className="odr-btn rounded-sm bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-4 disabled:opacity-50"
                        data-testid="cancel-order-confirm-btn"
                    >
                        {busy ? "Cancelling…" : "Confirm cancellation"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// Loader wrapper that fetches full order detail (with lines) before opening RP modal.
function RpLoader({ orderId, onClose, onSaved }) {
    const [order, setOrder] = useState(null);
    useEffect(() => {
        api.get(`/orders/${orderId}`).then((r) => setOrder(r.data));
    }, [orderId]);
    if (!order) return null;
    return (
        <RecordProductionModal
            order={order}
            onClose={onClose}
            onSaved={(createdRp) => onSaved?.(createdRp, order)}
        />
    );
}

function LtLoader({ orderId, onClose, onGenerated }) {
    const [order, setOrder] = useState(null);
    useEffect(() => {
        api.get(`/orders/${orderId}`).then((r) => setOrder(r.data));
    }, [orderId]);
    if (!order) return null;
    return (
        <LoadTagsModal order={order} onClose={onClose} onGenerated={onGenerated} />
    );
}

function PlLoader({ orderId, onClose, onGenerated }) {
    const [order, setOrder] = useState(null);
    const [customer, setCustomer] = useState(null);
    useEffect(() => {
        api.get(`/orders/${orderId}`)
            .then(async (r) => {
                setOrder(r.data);
                try {
                    const c = await api.get(`/customers/${r.data.customer_id}`);
                    setCustomer(c.data);
                } catch (err) {
                    console.error("PlLoader: failed to load customer", err);
                    setCustomer({ contacts: [] });
                }
            })
            .catch((err) => console.error("PlLoader: failed to load order", err));
    }, [orderId]);
    if (!order || !customer) return null;
    return (
        <PackingListsModal
            order={order}
            customer={customer}
            onClose={onClose}
            onGenerated={onGenerated}
        />
    );
}
